import { Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';

import { DividerModule } from 'primeng/divider';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { PowderLabelService } from '../../services/powder-label.service';

/**
 * Color approval status enum values (matches backend ColorApprovalStatus)
 * Uses string values as returned by OData API
 */
export enum ColorApprovalStatus {
  Provisional = 'Provisional',
  Interim = 'Interim',
  Final = 'Final',
  Suspended = 'Suspended',
  Banned = 'Banned',
}

/**
 * Represents a color ban/restriction for a coating system.
 * Matches backend CoatingSystemColor structure.
 */
export interface CoatingSystemColor {
  colorNumber: string;
  family?: string;
  dateOfDeclaration?: string;
  dateOfCancellation?: string;
  pmCanProduceBannedUntil?: string;
  pmCanSellBannedUntil?: string;
  coaterCanUseBannedUntil?: string;
  pmCanProduceRemainingUntil?: string;
  pmCanSellRemainingUntil?: string;
  coaterCanUseRemainingUntil?: string;
  colorApprovalStatus?: string;
  referenceNumber?: string;
}

/**
 * Extended CertifiableSystem with powder-specific properties.
 * Matches backend CoatingSystem derived type.
 */
export interface CoatingSystemData {
  pCode?: string;
  pClass?: number;
  glossCategory?: number;
  structureFinish?: string;
  approvalType?: number;
  coatingMaterial?: number;
  metallicColoursApproved?: boolean;
  coatingSystemColors?: CoatingSystemColor[];
  bannedFamilies?: string[];
  popTopCoats?: string[];
}

@Component({
  selector: 'app-powder-attributes-section',
  standalone: true,
  imports: [DividerModule, ChipModule, TagModule, ButtonModule, TooltipModule],
  styles: [
    `
      .color-chip {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        border-radius: 1rem;
        font-size: 0.875rem;
        cursor: default;
      }

      .color-chip.editable {
        cursor: pointer;
      }

      .color-chip .edit-icon {
        display: inline-block;
        width: 0;
        opacity: 0;
        overflow: hidden;
        transition:
          width 0.15s ease,
          opacity 0.15s ease,
          margin-left 0.15s ease;
        margin-left: 0;
        font-size: 0.75rem;
      }

      .color-chip.editable:hover .edit-icon {
        width: 0.75rem;
        opacity: 1;
        margin-left: 0.5rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="powder-attributes-content">
      @if (showHeader()) {
        <h3 class="text-lg font-semibold mb-3">
          <i class="pi pi-cog mr-2"></i>
          Powder Coating Attributes
        </h3>
        <p-divider></p-divider>
      }

      <div class="grid">
        <!-- Technical Properties Column -->
        <div class="col-12 md:col-6">
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">P-Code</label>
            <div class="text-900">{{ system()?.pCode || '-' }}</div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Class</label>
            <div class="text-900">{{ labelService.getClassLabel($safeNavigationMigration(system()?.pClass)) }}</div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Gloss Category</label>
            <div class="text-900">
              {{ labelService.getGlossCategoryLabel($safeNavigationMigration(system()?.glossCategory)) }}
            </div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Structure Finish</label>
            <div class="text-900">
              {{ labelService.getStructureFinishLabel($safeNavigationMigration(system()?.structureFinish)) }}
            </div>
          </div>
        </div>

        <!-- Material & Approval Column -->
        <div class="col-12 md:col-6">
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Coating Material</label>
            <div class="text-900">
              {{ labelService.getCoatingMaterialLabel($safeNavigationMigration(system()?.coatingMaterial)) }}
            </div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Approval Type</label>
            <div class="text-900">
              {{ labelService.getApprovalTypeLabel($safeNavigationMigration(system()?.approvalType)) }}
            </div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Metallic Colours</label>
            <p-tag
              [value]="system()?.metallicColoursApproved ? 'Approved' : 'Not Approved'"
              [severity]="system()?.metallicColoursApproved ? 'success' : 'secondary'"
              [rounded]="true">
            </p-tag>
          </div>
          @if (hasPopTopCoats()) {
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">P/P Top Coat(s)</label>
              @for (topCoat of system()?.popTopCoats ?? []; track topCoat) {
                <p-tag [value]="topCoat" [rounded]="true" [severity]="'info'"></p-tag>
              }
            </div>
          }
        </div>
      </div>

      <!-- Colour Restrictions Section -->
      <div class="mt-4">
        <div class="flex justify-content-between align-items-center mb-3">
          <h4 class="text-base font-semibold m-0 text-orange-700">
            <i class="pi pi-palette mr-2"></i>
            Colour Restrictions
          </h4>
          @if (editable()) {
            <div class="flex gap-2">
              <p-button
                icon="pi pi-plus"
                label="Add Color"
                size="small"
                severity="secondary"
                [text]="true"
                pTooltip="Add banned/suspended color"
                tooltipPosition="top"
                (onClick)="onAddColor()">
              </p-button>
            </div>
          }
        </div>

        <!-- Banned Families -->
        @if (hasBannedFamilies()) {
          <div class="field mb-3">
            <div class="flex align-items-center gap-2 mb-2">
              <label class="block text-sm font-medium text-600">Banned Families</label>
            </div>
            <div class="flex flex-wrap gap-2">
              @for (family of system()?.bannedFamilies ?? []; track family) {
                <p-chip [label]="family" styleClass="bg-red-100 text-red-800"></p-chip>
              }
            </div>
          </div>
        }

        <!-- Banned Colors -->
        @if (bannedColors().length > 0) {
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-2">
              Banned Colours ({{ bannedColors().length }})
            </label>
            <div class="flex flex-wrap gap-2">
              @for (color of bannedColors(); track color.colorNumber) {
                <span
                  class="color-chip bg-red-100 text-red-800"
                  [class.editable]="editable()"
                  (click)="editable() && onEditColor(color)">
                  {{ formatColorLabel(color) }}
                  @if (editable()) {
                    <i class="pi pi-pencil edit-icon"></i>
                  }
                </span>
              }
            </div>
          </div>
        }

        <!-- Suspended Colors -->
        @if (suspendedColors().length > 0) {
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-2">
              Suspended Colours ({{ suspendedColors().length }})
            </label>
            <div class="flex flex-wrap gap-2">
              @for (color of suspendedColors(); track color.colorNumber) {
                <span
                  class="color-chip bg-orange-100 text-orange-800"
                  [class.editable]="editable()"
                  (click)="editable() && onEditColor(color)">
                  {{ formatColorLabel(color) }}
                  @if (editable()) {
                    <i class="pi pi-pencil edit-icon"></i>
                  }
                </span>
              }
            </div>
          </div>
        }

        <!-- Provisional Colors -->
        @if (provisionalColors().length > 0) {
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-2">
              Provisional Colours ({{ provisionalColors().length }})
            </label>
            <div class="flex flex-wrap gap-2">
              @for (color of provisionalColors(); track color.colorNumber) {
                <span
                  class="color-chip bg-yellow-100 text-yellow-800"
                  [class.editable]="editable()"
                  (click)="editable() && onEditColor(color)">
                  {{ formatColorLabel(color) }}
                  @if (editable()) {
                    <i class="pi pi-pencil edit-icon"></i>
                  }
                </span>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class PowderAttributesSectionComponent {
  readonly labelService = inject(PowderLabelService);

  system = input<CoatingSystemData | null | undefined>();
  showHeader = input(true);
  editable = input(false);

  // Output events for editing actions
  addColor = output<void>();
  editColor = output<CoatingSystemColor>();

  onAddColor(): void {
    this.addColor.emit();
  }

  onEditColor(color: CoatingSystemColor): void {
    this.editColor.emit(color);
  }

  hasBannedFamilies(): boolean {
    const families = this.system()?.bannedFamilies;
    return !!families && families.length > 0;
  }

  hasPopTopCoats(): boolean {
    const topCoats = this.system()?.popTopCoats;
    return !!topCoats && topCoats.length > 0;
  }

  hasBannedColours(): boolean {
    const colors = this.system()?.coatingSystemColors;
    return !!colors && colors.length > 0;
  }

  hasColorRestrictions(): boolean {
    return (
      this.hasBannedFamilies() ||
      this.bannedColors().length > 0 ||
      this.suspendedColors().length > 0 ||
      this.provisionalColors().length > 0
    );
  }

  bannedColors(): CoatingSystemColor[] {
    return (
      this.system()?.coatingSystemColors?.filter((c) => c.colorApprovalStatus === ColorApprovalStatus.Banned) ?? []
    );
  }

  suspendedColors(): CoatingSystemColor[] {
    return (
      this.system()?.coatingSystemColors?.filter((c) => c.colorApprovalStatus === ColorApprovalStatus.Suspended) ?? []
    );
  }

  provisionalColors(): CoatingSystemColor[] {
    return (
      this.system()?.coatingSystemColors?.filter((c) => c.colorApprovalStatus === ColorApprovalStatus.Provisional) ?? []
    );
  }

  formatColorLabel(color: CoatingSystemColor): string {
    let label = color.colorNumber;

    // Add reference number in brackets for non-coded colors
    if (color.referenceNumber) {
      label += ` [${color.referenceNumber}]`;
    }

    // Add family in parentheses if available
    if (color.family) {
      label += ` (${color.family})`;
    }

    return label;
  }
}
